import React from 'react';
import VirtualMachine from 'store/virtual-machine/types/virtual-machine';
import { ReactComponent as CentOs } from 'assets/icons_3/os/centos-1 1.svg';
import { ReactComponent as Debian } from 'assets/icons_3/os/debian-2 1.svg';
import { ReactComponent as Fedora } from 'assets/icons/os/fedora.svg';
import { ReactComponent as Oracle } from 'assets/icons_3/os/oracle-corporation-logo 1.svg';
import { ReactComponent as Rocky } from 'assets/icons_3/os/rocky-linux 1.svg';
import { ReactComponent as Ubuntu } from 'assets/icons_3/os/ubuntu.svg';
import { ReactComponent as Windows } from 'assets/icons_3/os/microsoft.svg';
import { ReactComponent as All } from 'assets/icons/os/all.svg';
import { ReactComponent as Alma } from 'assets/icons_3/os/almalinux-icon-logo-1 1.svg';
import {
  IDiskSpeedDto,
  IOSTypeDto,
} from 'store/virtual-machine/types/api-bodies.types';
import { IOSTemplateDto } from 'components/OSTemplates/types/dtos';
import { Currency } from 'hooks/money-formatter';

export enum ActionTypes {
  SET_SINGLE_VM = 'vm: set-single',
  CLEAR_SINGLE_VM = 'vm: clear-single',
  SET_STATUS = 'vm: set-status',
  SET_LIST = 'vm: set-list',
  SET_REGIONS = 'vm: set-regions',
  SET_TEMPLATES = 'vm: set-templates',
  SET_CUSTOM_TEMPLATES = 'vm: set-custom-templates',
  SET_PARAM_LIMITS = 'vm: set-param-limits',
  SET_DISK_SIZE = 'vm: set-disk-size',
  DISK_DELETE = 'vm: set-disk-delete',
  AS_BOOT_DISK = 'vm: as-boot-disk',
  EDIT_NETWORK = 'vm edit-network',
  SET_OS_TYPES = 'vm: set-os-types',
  SET_TEMPLATE_MAX_SIZE = 'vm: set-template-max-size',
  SET_DISK_SPEED = 'vm: set-disk-speed',
}

export enum OSTypeId {
  'init',
  'ubuntu',
  'windows',
  'cent',
  'oracle',
  'debian',
  'rocky',
  'alma',
  'fedora',
  'custom',
}

type OSType = {
  [key in OSTypeId]: {
    name: string;
    Icon: JSX.Element;
  };
};

export const OS_TYPES: OSType = {
  [OSTypeId.init]: {
    name: 'All',
    Icon: <All />,
  },
  [OSTypeId.custom]: {
    name: 'EmptyOs',
    Icon: <All />,
  },
  [OSTypeId.debian]: {
    name: 'Debian',
    Icon: <Debian />,
  },
  [OSTypeId.windows]: {
    name: 'Windows',
    Icon: <Windows />,
  },
  [OSTypeId.cent]: {
    name: 'Cent OS',
    Icon: <CentOs />,
  },
  [OSTypeId.fedora]: {
    name: 'Fedora',
    Icon: <Fedora />,
  },
  [OSTypeId.oracle]: {
    name: 'Oracle Linux',
    Icon: <Oracle />,
  },
  [OSTypeId.rocky]: {
    name: 'Rocky Linux',
    Icon: <Rocky />,
  },
  [OSTypeId.ubuntu]: {
    name: 'Ubuntu',
    Icon: <Ubuntu />,
  },
  [OSTypeId.alma]: {
    name: 'Alma Linux',
    Icon: <Alma />,
  },
} as const;

export enum VMStatus {
  New = 'STATUS_NEW',
  Success = 'STATUS_SUCCESS',
  Starting = 'STATUS_STARTING',
  Started = 'STATUS_RUNNING',
  Stopping = 'STATUS_STOPPING',
  Stopped = 'STATUS_STOPPED',
  Deleted = 'STATUS_DELETED',
  Fail = 'STATUS_FAIL',
  ShuttingDown = 'STATUS_PROCESS_SHUTDOWN',
  Rebooting = 'STATUS_REBOOTING',
  OnPause = 'STATUS_PAUSE',
  Hibernated = 'STATUS_HIBERNATE',
  BackupRestore = 'STATUS_BACKUP_RESTORE',
  SnapshotRestore = 'STATUS_SNAPSHOT_ROLLBACK',
}

export const vmStatusDisabledDiskList = [
  VMStatus.Success,
  VMStatus.Starting,
  VMStatus.Started,
  VMStatus.Stopping,
  VMStatus.ShuttingDown,
  VMStatus.Rebooting,
  VMStatus.OnPause,
  VMStatus.Hibernated,
  VMStatus.BackupRestore,
  VMStatus.SnapshotRestore,
];

export enum VMStateAction {
  Shutdown = 'SHUTDOWN',
  Reboot = 'REBOOT',
  Pause = 'PAUSE',
  Hibernate = 'HIBERNATE',
  Stop = 'STOP',
  Reset = 'RESET',
  Start = 'START',
  Resume = 'RESUME',
}

export enum DiskType {
  HDD = 'HDD',
  SSD = 'SSD',
  NVME = 'NVME',
}

export const diskTypeNamesShort = {
  [DiskType.HDD]: 'disk.disk_types.hdd',
  [DiskType.SSD]: 'disk.disk_types.ssd',
  [DiskType.NVME]: 'disk.disk_types.nvme',
};
export const diskTypeNamesLong = {
  [DiskType.HDD]: 'disk.disk_types.hdd_long',
  [DiskType.SSD]: 'disk.disk_types.ssd_long',
  [DiskType.NVME]: 'disk.disk_types.nvme_long',
};

export interface IVMTemplate {
  vmTemplateId: number;
  osName: string;
  description: string;
  custom: boolean;
  sshAvail: boolean;
  isRootPassAvail: boolean
  osTypeId: OSTypeId;
  defaultDiskSize: number;
  isUserTemplate: boolean
}

export enum LimitedVMParam {
  CPUCores = 'cpu_cores',
  RAM = 'ram',
  HDDSize = 'hdd_size',
  SSDSize = 'ssd_size',
  NVMESize = 'nvme_size',
  PublicIP = 'public_ip',
  LocalNetworks = 'vlan',
  Backup = 'backup',
  Snapshot = 'snapshot',
  ScheduledBackup = 'scheduled_backup',
  BackupQuota = 'backup_quota',
  CustomTemplates = 'custom_image',
  Storage = 's3_storage',
  StorageTraffic = 's3_traffic',
  LoadBalancerPublic = 'loadbalancer_public',
  LoadBalancerLocal = 'loadbalancer_local',
  LoadBalancer = 'Load Balancers',
  ClusterControlGroup = 'Kubernetes Control Plane Group',
  MasterNodeHaConf1 = 'master_node_ha_conf_1',
  MasterNodeHaConf2 = 'master_node_ha_conf_2',
  MasterNodeHaConf3 = 'master_node_ha_conf_3',
  MasterNodeConf1 = 'master_node_conf_1',
  MasterNodeConf2 = 'master_node_conf_2',
  MasterNodeConf3 = 'master_node_conf_3',
  VmUserTemplate = 'vm_template',
  DatabaseCpu = 'managed_db_cpu',
  DatabaseRam = 'managed_db_ram',
  DatabaseDisk = 'managed_db_disk',
}

export const VM_PARAM_HIDDEN_REPORT = [
  LimitedVMParam.ScheduledBackup,
  LimitedVMParam.Snapshot,
  LimitedVMParam.CustomTemplates,
  LimitedVMParam.LoadBalancerPublic,
  LimitedVMParam.LoadBalancerLocal,
];

export type IParamLimits = {
  [key in LimitedVMParam]?: {
    max: number;
    min: number;
    price: number;
    maxRatio?: number;
    minRatio?: number;
    limit: number;
    currencyCode: Currency
  };
};

export type ParamNames = {
  [key in LimitedVMParam]: string;
};

export const limitedVMParamNames: ParamNames = {
  [LimitedVMParam.CPUCores]: `report.resources.${LimitedVMParam.CPUCores}`,
  [LimitedVMParam.RAM]: `report.resources.${LimitedVMParam.RAM}`,
  [LimitedVMParam.HDDSize]: `report.resources.${LimitedVMParam.HDDSize}`,
  [LimitedVMParam.SSDSize]: `report.resources.${LimitedVMParam.SSDSize}`,
  [LimitedVMParam.NVMESize]: `report.resources.${LimitedVMParam.NVMESize}`,
  [LimitedVMParam.PublicIP]: `report.resources.${LimitedVMParam.PublicIP}`,
  [LimitedVMParam.LocalNetworks]: `report.resources.${LimitedVMParam.LocalNetworks}`,
  [LimitedVMParam.Backup]: `report.resources.${LimitedVMParam.Backup}`,
  [LimitedVMParam.ScheduledBackup]: `report.resources.${LimitedVMParam.ScheduledBackup}`,
  [LimitedVMParam.Snapshot]: `report.resources.${LimitedVMParam.Snapshot}`,
  [LimitedVMParam.BackupQuota]: `report.resources.${LimitedVMParam.Backup}`,
  [LimitedVMParam.CustomTemplates]: `report.resources.${LimitedVMParam.CustomTemplates}`,
  [LimitedVMParam.Storage]: `report.resources.${LimitedVMParam.Storage}`,
  [LimitedVMParam.StorageTraffic]: `report.resources.${LimitedVMParam.StorageTraffic}`,
  [LimitedVMParam.LoadBalancerPublic]: `report.resources.${LimitedVMParam.LoadBalancerPublic}`,
  [LimitedVMParam.LoadBalancerLocal]: `report.resources.${LimitedVMParam.LoadBalancerLocal}`,
  [LimitedVMParam.LoadBalancer]: 'report.resources.load_balancer',
  [LimitedVMParam.ClusterControlGroup]: 'report.resources.kuber_groupClusterControlGroup',
  [LimitedVMParam.MasterNodeHaConf1]: `report.resource_component.${LimitedVMParam.MasterNodeHaConf1}`,
  [LimitedVMParam.MasterNodeHaConf2]: `report.resource_component.${LimitedVMParam.MasterNodeHaConf2}`,
  [LimitedVMParam.MasterNodeHaConf3]: `report.resource_component.${LimitedVMParam.MasterNodeHaConf3}`,
  [LimitedVMParam.MasterNodeConf1]: `report.resource_component.${LimitedVMParam.MasterNodeConf1}`,
  [LimitedVMParam.MasterNodeConf2]: `report.resource_component.${LimitedVMParam.MasterNodeConf2}`,
  [LimitedVMParam.MasterNodeConf3]: `report.resource_component.${LimitedVMParam.MasterNodeConf3}`,
  [LimitedVMParam.VmUserTemplate]: `report.resource_component.${LimitedVMParam.VmUserTemplate}`,
  [LimitedVMParam.DatabaseCpu]: `report.resource_component.${LimitedVMParam.DatabaseCpu}`,
  [LimitedVMParam.DatabaseRam]: `report.resource_component.${LimitedVMParam.DatabaseRam}`,
  [LimitedVMParam.DatabaseDisk]: `report.resource_component.${LimitedVMParam.DatabaseDisk}`,
};

export const limitedVMParamColors: ParamNames = {
  [LimitedVMParam.CPUCores]: '#32B58E',
  [LimitedVMParam.RAM]: '#BEEDAA',
  [LimitedVMParam.HDDSize]: '#F09253',
  [LimitedVMParam.SSDSize]: '#ED7471',
  [LimitedVMParam.NVMESize]: '#BD7471',
  [LimitedVMParam.PublicIP]: '#FDF287',
  [LimitedVMParam.LocalNetworks]: '#F092DD',
  [LimitedVMParam.Backup]: '#6B59C2',
  [LimitedVMParam.BackupQuota]: '#6B59C2',
  [LimitedVMParam.ScheduledBackup]: '#4C1A57',
  [LimitedVMParam.Snapshot]: '#006494',
  [LimitedVMParam.CustomTemplates]: '#AD2D2A',
  [LimitedVMParam.Storage]: '#8c4d23',
  [LimitedVMParam.StorageTraffic]: '#c75408',
  [LimitedVMParam.LoadBalancerPublic]: '#c75408',
  [LimitedVMParam.LoadBalancerLocal]: '#c75408',
  [LimitedVMParam.LoadBalancer]: '#c75408',
  [LimitedVMParam.ClusterControlGroup]: '#c75408',
  [LimitedVMParam.MasterNodeHaConf1]: '#E85D75',
  [LimitedVMParam.MasterNodeHaConf2]: '#D24C68',
  [LimitedVMParam.MasterNodeHaConf3]: '#B03E5E',
  [LimitedVMParam.MasterNodeConf1]: '#F6A04D',
  [LimitedVMParam.MasterNodeConf2]: '#D77D3D',
  [LimitedVMParam.MasterNodeConf3]: '#BB6334',
  [LimitedVMParam.VmUserTemplate]: '#4F7CAC',
  [LimitedVMParam.DatabaseCpu]: '#00B4D8',
  [LimitedVMParam.DatabaseRam]: '#2A9D8F',
  [LimitedVMParam.DatabaseDisk]: '#577590',
};

export type ResourcesPrice = {
  [key in LimitedVMParam]?: {
    count: number;
    price: number;
  };
};

export interface IDisk {
  id: number;
  name: string | null;
  size: number;
  diskType: DiskType;
  boot: boolean;
}
export interface IVMState {
  list: Array<VirtualMachine>;
  single: VirtualMachine | null;
  templates: Array<IVMTemplate>;
  customTemplates: Array<IOSTemplateDto>;
  templateMaxSize: number;
  osTypes: Array<IOSTypeDto>;
  paramLimits: IParamLimits;
  diskSpeed: {
    [DiskType.HDD]: IDiskSpeedDto;
    [DiskType.SSD]: IDiskSpeedDto;
    [DiskType.NVME]: IDiskSpeedDto;
  } | null;
}

export enum VMRoleId {
  SKIP,
  VM,
  SKIP_1,
  WorkerNode,
  ASG = 12,
}

export type VMRole = {
  id: VMRoleId;
  nameRu: string | null;
  nameEng: string | null;
  desc: string | null;
};
